import React, { useEffect, useState } from "react";
import CauseForImage from "./causeForConcernForms/cause-for-image";
import ConcernForIncident from "./causeForConcernForms/cause-for-incident";
import CauseForWitness from "./causeForConcernForms/cause-for-witness";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const CauseForConcern = ({ taskId }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    incidentInfo: {},
    witnessInfo: {},
    imageInfo: {},
  });
  const [manager, setManager] = useState("");
  const navigate = useNavigate();

  const stepKey = `causeConcernStep-${taskId}`;
  const formDataKey = `causeConcernFormData-${taskId}`;

  useEffect(() => {
    const savedFormData = localStorage.getItem(formDataKey);
    const savedStep = localStorage.getItem(stepKey);

    if (savedFormData) {
      try {
        setFormData(JSON.parse(savedFormData));
      } catch (err) {
        console.warn("Invalid saved form data");
      }
    }

    if (savedStep) {
      setCurrentStep(parseInt(savedStep, 10));
    }

    const fetchUsers = async () => {
      try {
        const response = await axios.get("/api/auth/users");
        const lineManager = response.data.find((user) => user.roleid === 3);
        setManager(lineManager || null);
      } catch (error) {
        console.error("Error fetching manager:", error);
      }
    };

    fetchUsers();
  }, [taskId]);

  useEffect(() => {
    localStorage.setItem(formDataKey, JSON.stringify(formData));
    localStorage.setItem(stepKey, currentStep.toString());
  }, [formData, currentStep]);

  const nextStep = () => setCurrentStep((prev) => prev + 1);
  const prevStep = () => setCurrentStep((prev) => prev - 1);

  const updateFormData = (newData) => {
    setFormData((prev) => ({
      ...prev,
      ...newData,
    }));
  };

  const handleRoomSearch = async (concernId) => {
    try {
      const todayDate = new Date().toISOString().split("T")[0];
      await axios.post(
        "/api/room/roomSearch",
        {
          witnessId: manager?.id,
          taskName: `Room Search ${todayDate}`,
          concernId: concernId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      console.error("Error submitting room search:", error);
    }
  };

  const updateDailyLogStatus = async () => {
    try {
      await axios.put(`/api/dailyLog/log/${taskId}`, {
        status: "completed",
      });
    } catch (error) {
      console.error("Error updating daily log status:", error);
      alert("Failed to update daily log status.");
    }
  };

  const handleSubmit = async () => {
    try {
      const response = await axios.post(
        "/api/cause/concern",
        { ...formData, dailyLogId: taskId },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const concernId = response?.data?.id;
      if (!concernId) {
        throw new Error("Concern ID not returned.");
      }

      await handleRoomSearch(concernId);
      await updateDailyLogStatus();

      localStorage.removeItem(formDataKey);
      localStorage.removeItem(stepKey);

      navigate("/daily-logs");
    } catch (error) {
      console.error("Error submitting concern or room search:", error);
    }
  };

  const renderForm = () => {
    switch (currentStep) {
      case 0:
        return (
          <ConcernForIncident
            formData={formData.incidentInfo}
            heading={formData?.title}
            updateFormData={updateFormData}
            nextStep={nextStep}
          />
        );
      case 1:
        return (
          <CauseForWitness
            formData={formData.witnessInfo}
            witness={formData?.witnessData}
            time={formData?.incidentTime}
            date={formData?.dateInformed}
            location={formData?.place}
            updateFormData={updateFormData}
            prevStep={prevStep}
            nextStep={nextStep}
          />
        );
      case 2:
        return (
          <CauseForImage
            formData={formData.imageInfo}
            files={formData?.uploadedFiles}
            images={formData?.uploadedImages}
            person={formData?.gender}
            updateFormData={updateFormData}
            prevStep={prevStep}
            handleSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  };

  return <div>{renderForm()}</div>;
};

export default CauseForConcern;
